/**
 * A single settings card slot: its header, plus whatever `children` its
 * content owner passes.
 *
 * Seven of these, in a fixed order, are the whole pool settings screen body.
 * This component is never handed an empty slot any more: `visibleSlotsForRole`
 * drops a slot whose content has not been built, so a header-only card cannot
 * reach the screen. `children` stays optional only because the branch that
 * fills it lives in the parent.
 *
 * Content owners, as of this writing: ISSUE-201 (Pool, filled), ISSUE-192
 * (Joining, filled), ISSUE-200 (Contest rules, filled), ISSUE-501 (Season,
 * filled), ISSUE-482 (Awards, filled), ISSUE-1040 (Chat, filled), ISSUE-817
 * (Danger zone, filled), ISSUE-1042 (Contests, filled), ISSUE-490
 * (Notifications, unbuilt). Members has no filed owner: ISSUE-817's member
 * role actions shipped on the members pane at `/pools?view=members`, not in a
 * settings card, so nothing is planned for the `members` slot here.
 */
import { Card, cn } from "@sportsmet/ui";
import { useEffect, useId, useState, type ReactNode } from "react";

import {
  GearIcon,
  PersonPlusIcon,
  ChatDotsIcon,
  TrophyIcon,
  ClipboardCheckIcon,
} from "../../shell/SectionIcon";
import type { typePoolSettingsSlot } from "./poolSettingsState";

export type typePoolSettingsCardSlotProps = {
  slot: typePoolSettingsSlot;
  /** True when `?panel=` names this slot. Deep-link target marker only. */
  isActive: boolean;
  /** The slot's content. Omitted while the panel's owning issue is unbuilt. */
  children?: ReactNode | undefined;
};

export const PoolSettingsCardSlot = ({
  slot,
  isActive,
  children,
}: typePoolSettingsCardSlotProps): ReactNode => {
  const [expanded, setExpanded] = useState(
    isActive || slot.panel === "pool" || slot.panel === "joining",
  );
  const contentId = useId();
  useEffect(() => {
    if (isActive) setExpanded(true);
  }, [isActive]);
  const descriptions: Record<string, string> = {
    pool: "Name and appearance",
    joining: "Access, approvals and sharing",
    "contest-rules": "How this contest is played",
    contests: "Current and upcoming contests",
    season: "Season and past results",
    awards: "Recognize your members",
    chat: "Conversation and posting permissions",
    "danger-zone": "Ownership and closing this pool",
  };
  const Icon =
    slot.panel === "joining"
      ? PersonPlusIcon
      : slot.panel === "chat"
        ? ChatDotsIcon
        : slot.panel === "awards" || slot.panel === "season"
          ? TrophyIcon
          : slot.panel === "contests" || slot.panel === "contest-rules"
            ? ClipboardCheckIcon
            : GearIcon;
  return (
    <Card
      data-testid={`pool-settings-slot-${slot.panel}`}
      data-active={isActive ? "true" : undefined}
      tone={slot.isDanger ? "error" : "default"}
      className={cn(slot.isDanger && "mt-6")}
    >
      <h2 aria-label={slot.title}>
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={contentId}
          onClick={() => {
            setExpanded(!expanded);
          }}
          className="flex min-h-12 w-full items-center gap-3 text-left"
        >
          <span
            aria-hidden="true"
            className="rounded-lg bg-primary-tint p-2 text-primary"
          >
            <Icon className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-body-strong text-ink">
              {slot.title}
            </span>
            <span className="block text-body text-ink-secondary">
              {descriptions[slot.panel]}
            </span>
          </span>
          <span aria-hidden="true">{expanded ? "−" : "+"}</span>
        </button>
      </h2>
      <div id={contentId} hidden={!expanded}>
        {children}
      </div>
    </Card>
  );
};
